import { HTMLAttributes, useCallback } from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Avatar,
  Checkbox,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import LabeledTextField from '../LabeledTextField';

export interface ComboboxOption {
  value: string;
  label: string;
  icon?: string;
  subLabel?: string;
  rightLabel?: string;
}

interface ComboboxProps {
  label: string;
  loading?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  inputValue?: string;
  placeholder?: string;
  fetchMore?: () => void;
  options: ComboboxOption[];
  onInputChange?: (value: string) => void;
  value: ComboboxOption | ComboboxOption[] | null;
  onChange: (value: ComboboxOption | ComboboxOption[] | null) => void;
}

const SCROLL_THRESHOLD = 5;

export function Combobox({
  label,
  value,
  options,
  onChange,
  fetchMore,
  inputValue,
  onInputChange,
  loading = false,
  multiple = false,
  disabled = false,
  placeholder = 'Search',
}: ComboboxProps) {
  const onScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight + SCROLL_THRESHOLD) {
        if (fetchMore) fetchMore();
      }
    },
    [fetchMore],
  );

  const renderOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, option: ComboboxOption, { selected }: { selected: boolean }) => {
      return (
        <li {...props} key={option.value}>
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" width={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {multiple && <Checkbox checked={selected} sx={{ p: 0 }} />}
              {option.icon && <Avatar src={option.icon} sx={{ width: 20, height: 20 }} />}
              <Stack>
                <Typography variant="body2">{option.label}</Typography>
                {option.subLabel && (
                  <Typography variant="inputLabel" color="text.muted">
                    {option.subLabel}
                  </Typography>
                )}
              </Stack>
            </Stack>
            {option.rightLabel && (
              <Typography variant="caption" color="text.muted">
                {option.rightLabel}
              </Typography>
            )}
          </Stack>
        </li>
      );
    },
    [multiple],
  );

  const renderTags = useCallback(
    (val: ComboboxOption[]) =>
      val.map((option) => (
        <Chip
          size="small"
          sx={{ m: 0.25 }}
          key={option.value}
          label={option.label}
          onDelete={() => onChange(val.filter((v) => v.value !== option.value))}
          icon={option.icon ? <Avatar src={option.icon} sx={{ width: 16, height: 16 }} /> : undefined}
        />
      )),
    [onChange],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <LabeledTextField {...params} loading={loading} label={label} disabled={disabled} placeholder={placeholder} />
    ),
    [label, loading, disabled, placeholder],
  );

  return (
    <Autocomplete
      fullWidth
      size="small"
      value={value}
      disablePortal
      options={options}
      loading={loading}
      disabled={disabled}
      multiple={multiple}
      inputValue={inputValue}
      renderTags={renderTags}
      filterOptions={(v) => v}
      renderInput={renderInput}
      ListboxProps={{ onScroll }}
      renderOption={renderOption}
      disableCloseOnSelect={multiple}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      loadingText={<LinearProgress color="inherit" sx={{ height: 2 }} />}
      onChange={(_, val) => onChange(val as ComboboxOption | ComboboxOption[])}
      onInputChange={(_, val, reason) => reason === ('input' || 'clear') && onInputChange?.(val)}
      getOptionLabel={(option) => (Array.isArray(option) ? option.map((o) => o.label).join(', ') : option.label)}
    />
  );
}
